<template>
  <div class="versions-layouts-wrapper">
    <div class="dbh-split-btn dbh-export-menu">
      <button
        type="button"
        class="dbh-btn dbh-btn--outline dbh-split-btn__main text-sm"
        @click="openModal"
      >
        <i class="bi bi-folder2-open" />
        Open
      </button>
      <button
        type="button"
        class="dbh-btn dbh-btn--outline dbh-split-btn__caret"
        aria-label="More open options"
        data-display-builder-export-toggle
        aria-expanded="false"
      >
        <i class="bi bi-chevron-down" />
      </button>
      <div
        class="dbh-dropdown__panel dbh-export-menu__panel"
        data-display-builder-export-menu
      >
        <button
          type="button"
          class="new-menu-item text-sm"
          @click="openNewVersionModal"
        >
          <i class="bi bi-plus-lg mr-1" />
          New Version
        </button>
      </div>
    </div>

    <transition name="fade">
      <div
        v-if="showModal"
        class="modal-backdrop-custom"
        @click.self="closeModal"
      />
    </transition>

    <transition name="slide-up">
      <div
        v-if="showModal"
        class="modal-custom"
        role="dialog"
        aria-modal="true"
      >
        <div class="modal-header-custom">
          <h5 class="modal-title-custom">
            Versions &amp; Layouts
          </h5>
          <button
            class="show-details-btn"
            type="button"
            @click="toggleDetails"
          >
            {{ showDetails ? 'Hide details' : 'Show details' }}
          </button>
          <button
            class="modal-close-btn"
            aria-label="Close"
            type="button"
            @click="closeModal"
          >
            <i class="bi bi-x-lg" />
          </button>
        </div>

        <div class="modal-tabs">
          <button
            class="tab-btn"
            :class="{ active: activeTab === 'versions' }"
            type="button"
            @click="activeTab = 'versions'"
          >
            Versions <span class="tab-badge">{{ versions.length }}</span>
          </button>
          <button
            class="tab-btn"
            :class="{ active: activeTab === 'layouts' }"
            type="button"
            @click="activeTab = 'layouts'"
          >
            Layouts <span class="tab-badge">{{ layouts.length }}</span>
          </button>
        </div>

        <div class="modal-body-custom">
          <div v-if="activeTab === 'versions'">
            <div class="list-header">
              <p class="tab-description mb-0">
                Pick a Version to load, or create a new one.
              </p>
              <button
                class="dbh-btn dbh-btn--outline"
                type="button"
                @click="openNewVersionModal"
              >
                <i class="bi bi-plus-lg" />
                New Version
              </button>
            </div>
            <p
              v-if="isLoading"
              class="item-meta mt-4 text-center"
            >
              Loading...
            </p>
            <p
              v-if="!versions.length && !isLoading"
              class="item-meta mt-4 text-center"
            >
              No versions saved yet.
            </p>
            <template v-if="!isLoading && versions.length">
              <div
                v-for="version in versions"
                :key="version.id"
                class="item-row"
                :class="{
                  selected: isCurrentVersion(version),
                  'is-editing': isEditingItem(version),
                }"
                @click="handleItemClick(version)"
              >
                <span class="item-grid-icon">
                  <i :class="isCurrentVersion(version) ? 'bi bi-check-lg' : 'bi bi-folder2-open'" />
                </span>
                <span class="item-info">
                  <input
                    v-if="isEditingItem(version)"
                    v-model="editingName"
                    class="item-name-input"
                    type="text"
                    maxlength="255"
                    :aria-label="`Rename ${version.name}`"
                    @click.stop
                    @keydown.enter.prevent.stop="saveRename(version)"
                    @keydown.esc.prevent.stop="cancelRename"
                  >
                  <span
                    v-else
                    class="item-name"
                  >{{ version.name }}</span>
                  <span
                    v-if="!isEditingItem(version)"
                    class="item-meta"
                  >
                    Updated {{ formatDate(version.updatedAt) }}
                  </span>
                  <span
                    v-if="showDetails"
                    class="item-meta"
                  >
                    Created by {{ version.createdBy || 'Unknown' }} - {{ formatDate(version.createdAt) }}
                  </span>
                  <span
                    v-if="showDetails"
                    class="item-meta"
                  >
                    Last updated by {{ version.updatedBy || 'Unknown' }}
                  </span>
                </span>
                <span class="item-actions">
                  <button
                    type="button"
                    class="item-action-btn"
                    @click.stop="loadItem(version)"
                  >
                    Load
                  </button>
                  <button
                    v-if="isEditingItem(version)"
                    type="button"
                    class="item-action-btn item-action-btn--rename"
                    aria-label="Save version name"
                    title="Save name"
                    :disabled="isRenaming"
                    @click.stop="saveRename(version)"
                  >
                    <i class="bi bi-check-lg" />
                  </button>
                  <button
                    v-else
                    type="button"
                    class="item-action-btn item-action-btn--rename"
                    aria-label="Rename version"
                    title="Rename"
                    @click.stop="startRename(version)"
                  >
                    <i class="bi bi-pencil" />
                  </button>
                  <button
                    type="button"
                    class="item-action-btn item-action-btn--danger"
                    aria-label="Delete version"
                    @click.stop="deleteItem(version)"
                  >
                    <i class="bi bi-trash" />
                  </button>
                </span>
              </div>
            </template>
          </div>

          <div v-if="activeTab === 'layouts'">
            <div class="list-header">
              <p class="tab-description mb-0">
                Pick a Layout to load, or create a new one.
              </p>
              <button
                class="dbh-btn dbh-btn--outline"
                type="button"
                @click="newLayout"
              >
                <i class="bi bi-plus-lg" />
                New Layout
              </button>
            </div>
            <p
              v-if="isLoading"
              class="item-meta"
            >
              Loading...
            </p>
            <p
              v-else-if="!layouts.length"
              class="item-meta"
            >
              No layouts saved yet.
            </p>
            <div
              v-for="layout in layouts"
              :key="layout.id"
              class="item-row"
              :class="{ 'is-editing': isEditingItem(layout) }"
              @click="handleItemClick(layout)"
            >
              <span class="item-grid-icon">
                <i class="bi bi-grid" />
              </span>
              <span class="item-info">
                <input
                  v-if="isEditingItem(layout)"
                  v-model="editingName"
                  class="item-name-input"
                  type="text"
                  maxlength="255"
                  :aria-label="`Rename ${layout.name}`"
                  @click.stop
                  @keydown.enter.prevent.stop="saveRename(layout)"
                  @keydown.esc.prevent.stop="cancelRename"
                >
                <span
                  v-else
                  class="item-name"
                >{{ layout.name }}</span>
                <span
                  v-if="!isEditingItem(layout)"
                  class="item-meta"
                >
                  Updated {{ formatDate(layout.updatedAt) }}
                </span>
                <span
                  v-if="showDetails"
                  class="item-meta"
                >
                  Created by {{ layout.createdBy || 'Unknown' }} - {{ formatDate(layout.createdAt) }}
                </span>
                <span
                  v-if="showDetails"
                  class="item-meta"
                >
                  Last updated by {{ layout.updatedBy || 'Unknown' }}
                </span>
              </span>
              <span class="item-actions">
                <button
                  type="button"
                  class="item-action-btn"
                  @click.stop="loadItem(layout)"
                >
                  Apply
                </button>
                <button
                  v-if="isEditingItem(layout)"
                  type="button"
                  class="item-action-btn item-action-btn--rename"
                  aria-label="Save layout name"
                  title="Save name"
                  :disabled="isRenaming"
                  @click.stop="saveRename(layout)"
                >
                  <i class="bi bi-check-lg" />
                </button>
                <button
                  v-else
                  type="button"
                  class="item-action-btn item-action-btn--rename"
                  aria-label="Rename layout"
                  title="Rename"
                  @click.stop="startRename(layout)"
                >
                  <i class="bi bi-pencil" />
                </button>
                <button
                  type="button"
                  class="item-action-btn item-action-btn--danger"
                  aria-label="Delete layout"
                  @click.stop="deleteItem(layout)"
                >
                  <i class="bi bi-trash" />
                </button>
              </span>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { mapActions } from 'vuex';

export default {
  name: 'VersionsLayoutsModal',

  data() {
    return {
      showModal: false,
      showDetails: false,
      activeTab: 'versions',
      versions: [],
      layouts: [],
      isLoading: false,
      isStartingNewVersion: false,
      editingItemKey: null,
      editingName: '',
      isRenaming: false,
      handleSavedItemsUpdated: null,
    };
  },

  mounted() {
    this.handleSavedItemsUpdated = (event) => {
      this.setItems(event.detail || {});
    };
    window.addEventListener('display-builder:saved-items-updated', this.handleSavedItemsUpdated);

    if (window.displayBuilderSavedItems) {
      this.setItems(window.displayBuilderSavedItems);
    }
  },

  beforeDestroy() {
    window.removeEventListener('display-builder:saved-items-updated', this.handleSavedItemsUpdated);
  },

  methods: {
    ...mapActions('displayBuilder', [
      'enableEditMode',
    ]),
    async newLayout() {
      const started = await window.displayBuilderStartNewLayout?.();

      if (!started) {
        return;
      }

      this.enableEditMode();
      this.closeModal();
    },
    async openModal() {
      this.showModal = true;
      await this.refreshItems();
    },
    closeModal() {
      this.cancelRename();
      this.showModal = false;
    },
    toggleDetails() {
      this.showDetails = !this.showDetails;
    },
    setItems(items = {}) {
      this.versions = Array.isArray(items.versions) ? items.versions : [];
      this.layouts = Array.isArray(items.layouts) ? items.layouts : [];
    },
    async refreshItems() {
      if (!window.displayBuilderRefreshSavedItems) {
        return;
      }

      this.isLoading = true;

      try {
        this.setItems(await window.displayBuilderRefreshSavedItems());
      } catch (error) {
        console.error('[DisplayBuilder] Could not refresh saved items', error);
        toastr.error('Could not load versions and layouts');
      } finally {
        this.isLoading = false;
      }
    },
    async openNewVersionModal() {
      this.closeMenus();

      if (this.isStartingNewVersion) {
        return;
      }

      this.isStartingNewVersion = true;

      try {
        await window.displayBuilderStartNewVersion?.();
      } finally {
        this.isStartingNewVersion = false;
      }
    },
    async loadItem(item) {
      const canContinue = await window.displayBuilderConfirmUnsavedAction?.({
        message: `Load this ${item.type === 'layout' ? 'Layout' : 'Version'}? Unsaved changes on the current canvas will be lost.`,
      });

      if (canContinue === false) {
        return;
      }

      window.displayBuilderApplySavedItem?.(item);
      this.closeModal();
    },
    async deleteItem(item) {
      const canContinue = await window.displayBuilderConfirmUnsavedAction?.({
        message: `Delete this ${item.type === 'layout' ? 'Layout' : 'Version'}? Unsaved changes on the current canvas may be lost while switching views.`,
      });

      if (canContinue === false) {
        return;
      }

      window.displayBuilderDeleteSavedItem?.(item);
      this.closeModal();
    },
    handleItemClick(item) {
      if (this.isEditingItem(item)) {
        return;
      }

      this.loadItem(item);
    },
    getItemKey(item) {
      return `${item?.type}:${item?.id}`;
    },
    isEditingItem(item) {
      return this.editingItemKey === this.getItemKey(item);
    },
    startRename(item) {
      this.editingItemKey = this.getItemKey(item);
      this.editingName = item.name || '';

      this.$nextTick(() => {
        const input = this.$el.querySelector('.item-name-input');

        input?.focus();
        input?.select();
      });
    },
    cancelRename() {
      if (this.isRenaming) {
        return;
      }

      this.editingItemKey = null;
      this.editingName = '';
    },
    async saveRename(item) {
      if (!this.isEditingItem(item) || this.isRenaming) {
        return;
      }

      const name = this.editingName.trim();

      if (!name) {
        toastr.error('Name cannot be empty');
        this.$nextTick(() => this.$el.querySelector('.item-name-input')?.focus());
        return;
      }

      if (name === item.name) {
        this.cancelRename();
        return;
      }

      this.isRenaming = true;

      try {
        await window.displayBuilderRenameSavedItem?.(item, name);
        this.editingItemKey = null;
        this.editingName = '';
      } catch (error) {
        this.$nextTick(() => this.$el.querySelector('.item-name-input')?.focus());
      } finally {
        this.isRenaming = false;
      }
    },
    isCurrentVersion(version) {
      return Number(window.displayBuilderCurrentVersionId) === Number(version.id);
    },
    formatDate(value) {
      if (!value) {
        return 'Just now';
      }

      try {
        return new Intl.DateTimeFormat(undefined, {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
        }).format(new Date(value));
      } catch (error) {
        return value;
      }
    },
    closeMenus() {
      this.$el.querySelectorAll('[data-display-builder-export-menu]').forEach((menu) => {
        menu.classList.remove('is-open');
      });
      this.$el.querySelectorAll('[data-display-builder-export-toggle]').forEach((button) => {
        button.setAttribute('aria-expanded', 'false');
      });
    },
  },
};
</script>

<style scoped>
.versions-layouts-wrapper {
  position: relative;
  display: inline-block;
  font-family: 'Segoe UI', system-ui, -apple-system, sans-serif;
}

.modal-backdrop-custom {
  position: fixed;
  inset: 0;
  background: rgba(15, 15, 25, 0.45);
  z-index: 1040;
}

.modal-custom {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  max-width: 52rem;
  width: calc(100% - 32px);
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 20px 60px rgba(0,0,0,0.18);
  z-index: 1050;
  overflow: hidden;
}

.modal-header-custom {
  display: flex;
  align-items: center;
  padding: 24px 30px 12px;
  gap: 12px;
}

.modal-title-custom {
  font-size: 20px;
  font-weight: 700;
  color: #080e1d;
  margin: 0;
  flex: 1;
}

.show-details-btn,
.modal-close-btn {
  background: none;
  border: none;
  color: #667085;
  cursor: pointer;
  border-radius: 4px;
}

.show-details-btn {
  font-size: 13px;
  padding: 4px 8px;
}

.modal-close-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
}

.show-details-btn:hover,
.modal-close-btn:hover {
  color: #111827;
  background: #f3f4f6;
}

.modal-tabs {
  display: flex;
  padding: 0 30px;
  border-bottom: 1px solid #e4e7eb;
  gap: 26px;
}

.tab-btn {
  padding: 12px 0;
  background: none;
  border: none;
  border-bottom: 3px solid transparent;
  font-size: 15px;
  font-weight: 700;
  color: #667085;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: -1px;
}

.tab-btn.active {
  color: #080e1d;
  border-bottom-color: #5bc0a4;
}

.tab-badge {
  color: #667085;
  font-size: 13px;
  font-weight: 700;
}

.modal-body-custom {
  padding: 18px 30px 28px;
  max-height: 440px;
  overflow-y: auto;
}

.list-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 14px;
}

.tab-description {
  font-size: 14px;
  color: #667085;
  margin: 0 0 12px;
}

.item-row {
  display: flex;
  align-items: center;
  gap: 14px;
  width: 100%;
  padding: 12px;
  border-radius: 8px;
  transition: background 0.12s;
  border: 0;
  background: transparent;
  text-align: left;
}

.item-row:hover,
.item-row.selected {
  background: #f5f7fa;
  cursor: pointer;
}

.item-row:hover .item-actions {
  opacity: 1;
}

.item-row.is-editing {
  cursor: default;
}

.item-row.is-editing .item-actions {
  opacity: 1;
}

.item-grid-icon {
  color: #667085;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  flex-shrink: 0;
}

.item-row.selected .item-grid-icon {
  color: #5bc0a4;
}

.item-info {
  display: flex;
  flex-direction: column;
  flex: 1;
  gap: 2px;
  min-width: 0;
}

.item-name {
  font-size: 15px;
  font-weight: 700;
  color: #080e1d;
}

.item-name-input {
  width: 100%;
  min-width: 0;
  height: 30px;
  padding: 3px 8px;
  border: 1px solid #667085;
  border-radius: 6px;
  color: #080e1d;
  background: #ffffff;
  font: inherit;
  font-size: 15px;
  font-weight: 700;
  outline: none;
}

.item-name-input:focus {
  border-color: #080e1d;
  box-shadow: 0 0 0 2px rgba(8, 14, 29, 0.14);
}

.item-meta {
  font-size: 13px;
  color: #667085;
}

.item-actions {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-left: auto;
  flex-shrink: 0;
  opacity: 0;
}

.item-action-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 28px;
  height: 28px;
  padding: 0 6px;
  border: 0;
  border-radius: 6px;
  background: transparent;
  color: #50627a;
  font-size: 12px;
  cursor: pointer;
}

.item-action-btn:hover {
  background: #eef2f7;
  color: #080e1d;
}

.item-action-btn:disabled {
  cursor: wait;
  opacity: 0.55;
}

.item-action-btn--rename {
  color: #50627a;
}

.item-action-btn--danger {
  color: #ef4444;
}

.item-action-btn--danger:hover {
  background: #fef2f2;
  color: #dc2626;
}

.dbh-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 600;
  border-radius: 8px;
  border: 1px solid transparent;
  padding: 0.5rem 1rem;
  cursor: pointer;
  white-space: nowrap;
  line-height: 1;
}

.dbh-btn--outline {
  background: #ffffff;
  border-color: #e4e7eb;
  color: #080e1d;
}

.dbh-btn--outline:hover {
  background: #f8f9fb;
}

.dbh-split-btn {
  display: inline-flex;
  position: relative;
}

.dbh-split-btn__main {
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
}

.dbh-split-btn__caret {
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
  padding-left: 10px;
  padding-right: 10px;
  border-color: #e4e7eb !important;
}

.dbh-dropdown__panel {
  display: none;
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  min-width: 160px;
  background: #ffffff;
  border: 1px solid #e4e7eb;
  border-radius: 8px;
  box-shadow: 0 12px 30px rgba(16, 24, 40, 0.16);
  padding: 6px;
  z-index: 1060;
}

.dbh-dropdown__panel.is-open {
  display: block;
}

.new-menu-item {
  display: flex;
  align-items: center;
  width: 100%;
  padding: 9px 12px;
  border: 0;
  border-radius: 6px;
  background: #fff;
  color: #080e1d;
  font-size: 13px;
  text-align: left;
  cursor: pointer;
}

.new-menu-item:hover {
  background: #f8f9fb;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.18s ease;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}

.slide-up-enter-active,
.slide-up-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.slide-up-enter,
.slide-up-leave-to {
  opacity: 0;
  transform: translate(-50%, calc(-50% + 12px));
}
</style>
